import {
  selectedSolarInverterApplianceId,
  type SolarDesignDraftState,
} from './solarDesignDraft';
import { type SolarEquipmentSelection } from './solarElectricalTopology';
import { z } from 'zod/v4';

/** Scoped deterministic IDs for a one-time legacy upgrade, independent of geometry. */
const legacyId = (namespace: string, kind: number, ordinal: number) =>
  `${namespace.slice(0, 24)}${(kind * 1_000_000 + ordinal).toString(16).padStart(12, '0')}`;

export const legacySolarPanelId = (namespace: string, ordinal: number) =>
  legacyId(namespace, 3, ordinal);

/** Canonical restored identities only; every physical and electrical field is retained. */
export const restoreSolarPanelIdentities = (
  layout: SolarDesignDraftState['layout'],
  namespace: string
): SolarDesignDraftState['layout'] => ({
  ...layout,
  workingPanels: layout.workingPanels.map((panel, index) =>
    panel.id ? panel : { ...panel, id: legacySolarPanelId(namespace, index) }
  ),
});

export const upgradeSolarDesignElectrical = (
  state: SolarDesignDraftState,
  namespace: string,
  conventionalInverter = true
): SolarDesignDraftState & { error?: string } => {
  if (state.electrical.mode === 'allocated') return state;
  if (!z.uuid().safeParse(namespace).success)
    return {
      ...state,
      error: 'Use a valid design identity before enabling allocation.',
    };
  const faceIds = new Set(state.layout.workingFaces.map((face) => face.id));
  if (state.layout.workingPanels.some((panel) => !faceIds.has(panel.faceId)))
    return {
      ...state,
      error: 'Restore the missing roof before enabling allocation.',
    };
  const upgraded = structuredClone(state);
  const selection = (
    id: string,
    applianceId: string | null
  ): SolarEquipmentSelection => ({
    id,
    applianceId,
    productSpecId: null,
    contentHash: null,
    specSnapshot: null,
  });
  const panelSelections = upgraded.layout.workingFaces.map((face, index) =>
    selection(legacyId(namespace, 1, index), upgraded.selectedPanelApplianceId)
  );
  const groups = upgraded.layout.workingFaces.map((face, index) => ({
    id: legacyId(namespace, 2, index),
    faceId: face.id,
    equipmentSelectionId: panelSelections[index].id,
  }));
  const groupByFaceId = new Map(groups.map((group) => [group.faceId, group]));
  upgraded.layout.workingPanels = upgraded.layout.workingPanels.map(
    (panel, index) => {
      const group = groupByFaceId.get(panel.faceId)!;
      return {
        ...panel,
        id: panel.id ?? legacySolarPanelId(namespace, index),
        placementGroupId: group.id,
        equipmentSelectionId: group.equipmentSelectionId,
      };
    }
  );
  const inverterApplianceId = selectedSolarInverterApplianceId(
    upgraded.inverter
  );
  const inverterSelection =
    conventionalInverter && inverterApplianceId
      ? selection(legacyId(namespace, 4, 0), inverterApplianceId)
      : null;
  upgraded.electrical = {
    mode: 'allocated',
    version: 1,
    equipmentSelections: [
      ...panelSelections,
      ...(inverterSelection ? [inverterSelection] : []),
    ],
    placementGroups: groups,
    inverterInstances: inverterSelection
      ? [
          {
            id: legacyId(namespace, 5, 0),
            name: 'Inverter 1',
            equipmentSelectionId: inverterSelection.id,
            quantity: 1,
            trackers: [],
          },
        ]
      : [],
    strings: [],
    validation: null,
  };
  return upgraded;
};
